import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import type {
  Assignment,
  AssignmentRole,
  HistoryEntry,
  MeetingType,
  MemberSummary,
  Meeting,
  SpeakerType,
} from "../lib/types";

/* ---------------------------------------------------------------- */
/* Queries                                                          */
/* ---------------------------------------------------------------- */

export function useMembers() {
  return useQuery({
    queryKey: ["members"],
    queryFn: async (): Promise<MemberSummary[]> => {
      const { data, error } = await supabase
        .from("sacrament_member_summary")
        .select("*")
        .order("last_name")
        .order("first_name");
      if (error) throw error;
      return (data ?? []) as MemberSummary[];
    },
  });
}

interface MeetingRow extends Omit<Meeting, "assignments"> {
  sacrament_assignments: Assignment[];
}

export function useMeetings() {
  return useQuery({
    queryKey: ["meetings"],
    queryFn: async (): Promise<Meeting[]> => {
      const { data, error } = await supabase
        .from("sacrament_meetings")
        .select("*, sacrament_assignments(*)")
        .order("meeting_date", { ascending: false });
      if (error) throw error;
      return ((data ?? []) as MeetingRow[]).map((m) => ({
        id: m.id,
        meeting_date: m.meeting_date,
        meeting_type: m.meeting_type,
        notes: m.notes,
        created_at: m.created_at,
        updated_at: m.updated_at,
        assignments: [...(m.sacrament_assignments ?? [])].sort(
          (a, b) => a.position - b.position,
        ),
      }));
    },
  });
}

/** A member's talks and prayers, newest first. Derived from the meetings we
 *  already hold rather than a separate round trip. */
export function historyFor(memberId: string, meetings: Meeting[]): HistoryEntry[] {
  const out: HistoryEntry[] = [];
  for (const m of meetings) {
    for (const a of m.assignments) {
      if (a.member_id === memberId) {
        out.push({ date: m.meeting_date, role: a.role, topic: a.topic });
      }
    }
  }
  return out.sort((a, b) => b.date.localeCompare(a.date));
}

/* ---------------------------------------------------------------- */
/* Mutations                                                        */
/* ---------------------------------------------------------------- */

export interface SaveMeetingInput {
  meetingId: string | null;
  date: string;
  type: MeetingType;
  assignments: Array<{
    member_id: string | null;
    guest_name: string | null;
    role: AssignmentRole;
    topic: string | null;
    position: number;
  }>;
}

export function useSaveMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaveMeetingInput) => {
      const { data, error } = await supabase.rpc("sacrament_save_meeting", {
        p_meeting_id: input.meetingId,
        p_date: input.date,
        p_type: input.type,
        p_assignments: input.assignments,
      });
      if (error) throw error;
      return data as string;
    },
    // Both caches change: meetings gains a row, and every member's last-spoke
    // date is derived from meetings, so Up Next has to re-read too.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["meetings"] });
      void qc.invalidateQueries({ queryKey: ["members"] });
    },
  });
}

export interface MemberInput {
  id?: string;
  first_name: string;
  last_name: string;
  speaker_type: SpeakerType;
  status?: "active" | "moved_out";
}

export function useSaveMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (m: MemberInput) => {
      if (m.id) {
        const { error } = await supabase
          .from("sacrament_members")
          .update({
            first_name: m.first_name,
            last_name: m.last_name,
            speaker_type: m.speaker_type,
            status: m.status,
          })
          .eq("id", m.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("sacrament_members").insert({
          first_name: m.first_name,
          last_name: m.last_name,
          speaker_type: m.speaker_type,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["members"] }),
  });
}

export function useToggleConsidering() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ memberId, on }: { memberId: string; on: boolean }) => {
      if (on) {
        const { error } = await supabase
          .from("sacrament_considering")
          .insert({ member_id: memberId });
        if (error && error.code !== "23505") throw error; // already starred
      } else {
        const { error } = await supabase
          .from("sacrament_considering")
          .delete()
          .eq("member_id", memberId);
        if (error) throw error;
      }
    },
    // Optimistic: starring should feel instant in a meeting.
    onMutate: async ({ memberId, on }) => {
      await qc.cancelQueries({ queryKey: ["members"] });
      const prev = qc.getQueryData<MemberSummary[]>(["members"]);
      qc.setQueryData<MemberSummary[]>(["members"], (old) =>
        (old ?? []).map((m) => (m.id === memberId ? { ...m, is_considering: on } : m)),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(["members"], ctx.prev);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["members"] }),
  });
}

export function useClearConsidering() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("sacrament_considering")
        .delete()
        .not("member_id", "is", null);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["members"] }),
  });
}

/** Bulk insert for the initial roster import. */
export function useImportMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: Array<Omit<MemberInput, "id">>) => {
      const { error } = await supabase.from("sacrament_members").insert(rows);
      if (error) throw error;
      return rows.length;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["members"] }),
  });
}
